import { Box, HStack, Text } from 'design-system';
import type { SalesLookupFamilyRow } from 'apis/sales-lookup';
import { formatPrice } from 'pages/prices/utils/matrix-display';
import { vatCaption, VatMode, withVat } from 'pages/sales-lookup/utils/vat';

const sourceLabel: Record<'CLOSED' | 'DEAL' | 'MIXED', string> = {
  CLOSED: 'last closed',
  DEAL: 'deal',
  MIXED: 'last closed / deal',
};

export interface FamilyCardProps {
  family: SalesLookupFamilyRow;
  vatMode: VatMode;
  onClick: () => void;
}

export const FamilyCard = ({ family, vatMode, onClick }: FamilyCardProps) => {
  const range = family.priceRange;
  const initial = family.universalName.trim().charAt(0).toUpperCase() || '?';

  return (
    <HStack
      as='button'
      w='full'
      textAlign='start'
      spacing='12px'
      align='center'
      bg='white'
      borderRadius='14px'
      border='1px solid'
      borderColor='gray.100'
      p='14px'
      mb='10px'
      _hover={{ borderColor: 'gray.300' }}
      onClick={onClick}>
      <Box w='44px' h='44px' borderRadius='10px' bg='red.50' flexShrink={0} display='flex' alignItems='center' justifyContent='center'>
        <Text fontSize='16px' fontWeight='bold' color='red.600'>
          {initial}
        </Text>
      </Box>

      <Box flex='1' minW={0}>
        <Text fontSize='15px' fontWeight='semibold' color='gray.800' noOfLines={2}>
          {family.universalName}
        </Text>
        <Text fontSize='12px' color='gray.500' mt='2px' noOfLines={1}>
          {family.brandCount} brands · {family.supplierCount} suppliers
          {family.categoryName ? ` · ${family.categoryName}` : ''}
        </Text>
      </Box>

      <Box textAlign='end' flexShrink={0}>
        {range ? (
          <>
            <Text fontSize='14px' fontWeight='bold' color='gray.900' whiteSpace='nowrap'>
              AED {formatPrice(withVat(range.min, vatMode))}
              {range.max > range.min ? ` – ${formatPrice(withVat(range.max, vatMode))}` : ''}
            </Text>
            <Text fontSize='11px' color='gray.500'>
              / {range.unit} · {sourceLabel[range.source]} {vatCaption(vatMode)}
            </Text>
          </>
        ) : family.hasPrices ? (
          // Prices exist but the priced variants share no unit word, so there
          // is no honest single-unit range — point inside instead of lying.
          <Text fontSize='12px' fontWeight='semibold' color='gray.600'>
            Prices inside
          </Text>
        ) : (
          <Text fontSize='12px' color='gray.400'>
            No prices yet
          </Text>
        )}
      </Box>
    </HStack>
  );
};
